/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import React, { useState, useEffect, useRef, ReactElement, useMemo, Fragment } from 'react';
import styled, { CSSObject } from '@emotion/styled';
import { getPatternModal } from '../../../style';

import Avatar from '../../../atoms/Avatar';
import Headline from '../../common/Headline';
import TextLink from '../../common/TextLink';
import PriceInput, { adjustStylePerDigit } from './PriceInput';
import Button from '../../../atoms/buttons/Button';
import ButtonSet from '../../blocks/ButtonSet';
import { IsMobileViewPort } from '../../../utils/helpers';
import { reviseOffer, sendCancel, sendSuggest } from '../../../actions/profileTrackAction';
import useOnClickTriggerTracking from '../../../hooks/useOnclickTriggerTracking';
import borders from '../../../theme/borders';
import colors, { white } from '../../../theme/colors';
import { connect, useDispatch } from 'react-redux';
import { ProfileMaker } from '../../../models/Profile';
import { ProfileState } from '../../../reducers/profile';
import PrimaryButton from '../../../atoms/buttons/PrimaryButton';
import { CrossIcon } from '../../../atoms/icons/favoriteIcons';
import { PriceContainer, getInputProps, CurrencyTip, CurrencyLabelSpan } from './common';
import { RootState } from '../../../store';
import { updateOffer } from '../../../actions/offerActions';
import { clearErrors } from '../../../actions/commonActions';
import WYPButtonIcon from '../../../atoms/icons/WYPButtonIcon';
import OfferModalFooter from './OfferModalFooter';
import ModalWithPattern from 'components/common/modals/ModalWithPattern';
import { mobileOverlayStyle, mobileContentStyle } from 'components/common/CommonStyle';
import breakpoints from 'theme/material/breakpoints';
import { isMobile } from 'react-device-detect';

const EditAmountRange = styled('div')<{ hasErrors: boolean }>(
  {
    fontSize: 14,
    lineHeight: '19px',
    letterSpacing: '-0.3px',
    marginTop: 10,
    marginBottom: 20,
    textAlign: 'center',
    color: getPatternModal().headerColor,
  },
  ({ hasErrors }) => ({
    color: hasErrors ? colors.light.warning.primary : getPatternModal().headerColor,
    fontStyle: hasErrors ? 'italic' : 'inherit',
  })
);

const Form = styled('form')({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
});

const HeaderDiv = styled('div')({
  textAlign: 'center',

  [breakpoints.down(639.05)]: {
    marginTop: 10,
  },
});

const CenterDiv = styled('div')({
  textAlign: 'center',
  padding: '20px 0',
  lineHeight: '19px',
  letterSpacing: '-0.3px',
  color: getPatternModal().headerColor,
  [breakpoints.down(639.05)]: {
    padding: 15,
  },
});

const AvatarTitleDiv = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  flexWrap: 'wrap',
  justifyContent: 'center',
  margin: '0 auto',
  alignItems: 'center',
  gap: 20,
});

const TitleDiv = styled('span')(
  (): CSSObject => ({
    color: getPatternModal().titleColor,
    fontSize: 36,
    fontWeight: 800,
    lineHeight: '40px',
    letterSpacing: -0.3,
    textTransform: 'uppercase',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    [breakpoints.down(639.05)]: {
      fontSize: 30,
    },
  })
);

const Content = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  flexGrow: 1,
  justifyContent: 'space-between',
});

const colorType = (price, hasErrors) => {
  switch (price) {
    case true:
      return 'default';
    case false:
      return !hasErrors ? 'brand' : 'alert';
    default:
      return;
  }
};

const ButtonDiv = styled('div')<{ hasErrors: boolean }>({
  marginTop: isMobile ? 0 : 20,
});

const status = currentUserIsGenerous => (currentUserIsGenerous ? 'Offer' : 'Request');

const offerMinMaxVal = (offer, type, defaultAmount) => {
  const offerVal = offer && type === 'min' ? offer.data.min_counter_price : offer && offer.data.max_counter_price;

  return offer ? offerVal : defaultAmount;
};

const validateTextInput = e => {
  const Key = e.key;
  const regex = /[0-9]/;

  if (!regex.test(Key)) {
    if (e.preventDefault) e.preventDefault();
  }
};

interface OfferEditProps {
  isModalOpen: boolean;
  avatarSrc: any;
  offer?: any;
  isCounter?: any;
  closeModal: any;
  currencyLabel: any;
  currencyInitial: any;
  currentUserIsGenerous: any;
  username: any;
  hashId: any;
  buttonID: any;
  currentOffer: any;
  successOpen: any;
  sex: string;
  profile: ProfileState;
  onCancelAndSendNewOfferHandler: () => void;
  customContentStyle?: any;
}

const OfferEdit = ({
  avatarSrc,
  offer = '',
  closeModal,
  currencyLabel,
  currencyInitial,
  currentUserIsGenerous,
  username,
  hashId,
  buttonID,
  currentOffer,
  successOpen,
  sex,
  profile,
  onCancelAndSendNewOfferHandler,
  isModalOpen,
  customContentStyle,
}: OfferEditProps): ReactElement => {
  const dispatch = useDispatch();
  const [defaultValue] = useState('');
  const [offerPrice, setOfferPrice] = useState('');
  const [minValue, setMinValue] = useState(offerMinMaxVal(offer, 'min', '5'));
  const [maxValue, setMaxValue] = useState(offerMinMaxVal(offer, 'max', '500'));
  const [hasErrors, setHasErrors] = useState(false);
  const [isOfferSending, setOfferSending] = useState(false);

  const sendOfferBtn = useRef(null);
  const sendCancelBtn = useRef(null);
  const priceInput = useRef(null);
  const sendSuggestLink = useRef(null);
  const profileModel = ProfileMaker.create(profile.profile);
  const isGenerous = profileModel.isGenerous();
  const currLabel = String.fromCharCode(currencyLabel);

  const hasAmountOffer = currentOffer !== null && parseInt(currentOffer) !== 0;
  const isOfferAmountIsMinimumOrMaximum = isGenerous
    ? parseInt(currentOffer) === parseInt(maxValue)
    : parseInt(currentOffer) === parseInt(minValue);

  useOnClickTriggerTracking('Revise Offer', sendOfferBtn, reviseOffer);
  useOnClickTriggerTracking('Send Cancel', sendCancelBtn, sendCancel);
  useOnClickTriggerTracking('Suggest Offer', sendSuggestLink, sendSuggest);

  useEffect(() => {
    if (isOfferAmountIsMinimumOrMaximum && !hasErrors) {
      setHasErrors(true);
    }
  }, [isOfferAmountIsMinimumOrMaximum, hasErrors]);

  useEffect(() => {
    setMaxValue(offerMinMaxVal(offer, 'max', '500'));
    setMinValue(offerMinMaxVal(offer, 'min', '5'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offer]);

  const updateOfferPrice = (val, error) => {
    if (!error) {
      dispatch(clearErrors());
    }
    setOfferPrice(val);
    setHasErrors(error || false);
  };

  useEffect(() => {
    setOfferPrice(currentOffer);
  }, [currentOffer]);

  const offerAmountMessage = useMemo(() => {
    if (isOfferAmountIsMinimumOrMaximum) {
      return `Cannot edit your offer since it is already at the ${
        isGenerous ? 'maximum' : 'minimum'
      }. Cancel and resubmit a new offer instead?`;
    }

    const prefixMessage = hasErrors
      ? isGenerous
        ? 'You must enter a new offer between'
        : 'Your new offer request must be between'
      : `Enter a new offer ${isGenerous ? '' : 'request'} between`;

    const fromValue = isGenerous
      ? hasAmountOffer
        ? currentOffer + 1
        : minValue
      : hasAmountOffer
        ? currentOffer - 1
        : maxValue;
    const toValue = isGenerous ? maxValue : minValue;

    return `${prefixMessage} ${currencyInitial}${currLabel}${fromValue} and ${currencyInitial}${currLabel}${toValue}.`;
  }, [
    isOfferAmountIsMinimumOrMaximum,
    hasErrors,
    currentOffer,
    offerPrice,
    currencyInitial,
    currLabel,
    isGenerous,
    maxValue,
    minValue,
    hasAmountOffer,
  ]);

  const isValidateOfferAmountByType = offerAmount => {
    if (parseInt(offerAmount) === parseInt(currentOffer)) {
      return false;
    }

    // user is initially asking for offer suggestion from other account
    if (!hasAmountOffer) {
      return true;
    }

    if (parseInt(offerAmount) === parseInt(currentOffer)) {
      return false;
    }

    return profileModel.isGenerous() ? Boolean(offerAmount > currentOffer) : Boolean(offerAmount < currentOffer);
  };

  const inputOnChange = e => {
    const inputVal = Number(e.target.value);
    const minValueNum = Number(minValue);
    const maxValueNum = Number(maxValue);
    const maxLength = maxValueNum.toString().length;

    if (e.target.value.length <= maxLength) {
      const error = inputVal < minValueNum || inputVal > maxValueNum || !isValidateOfferAmountByType(inputVal);

      updateOfferPrice(inputVal, error);
    } else {
      e.preventDefault();
    }
  };

  const handleSubmit = async e => {
    setOfferSending(true);
    e.preventDefault();

    const formData = { price: offerPrice, account: hashId };

    try {
      const response = await dispatch(updateOffer(formData));
      const currencyInitial = response.currency_original.data.initial;
      const currLabel = String.fromCharCode(response.currency_original.data.label_unicode);
      const offerAmount = `${currencyInitial}${currLabel}${offerPrice}`;

      successOpen(true, offerAmount, false, true);
      setOfferSending(false);
    } catch {
      setOfferSending(false);
      setHasErrors(true);
    }
  };

  const onHandleSuggestion = async e => {
    setOfferSending(true);
    e.preventDefault();
    const formData = { price: 0, account: hashId, suggest: true };

    try {
      await dispatch(updateOffer(formData));
      successOpen(true, '', false, true);
      setOfferSending(false);
    } catch {
      setOfferSending(false);
      setHasErrors(true);
    }
  };

  const cancelAndShowANewOfferModal = () => {
    // trigger cancel and show new offer modal
    // close modal
    closeModal();
    onCancelAndSendNewOfferHandler();
  };

  return (
    <ModalWithPattern
      isModalOpen={isModalOpen}
      dataTestID="edit-offer"
      closeBtnClassName="edit-offer-modal-close"
      closeBtnDataTestId="edit-offer-modal-close"
      modalContentLabel="Edit Offer Modal"
      onCloseClick={closeModal}
      closeRequest={closeModal}
      customOverlayStyle={IsMobileViewPort ? mobileOverlayStyle : {}}
      customContentStyle={IsMobileViewPort ? { ...mobileContentStyle, ...customContentStyle } : customContentStyle}
      withoutBgPattern={false}
      hideCloseBtn={false}
      closeOnOverlayClick={true}
      enableTouchMove={true}
    >
      <Fragment>
        <Form>
          <HeaderDiv>
            <AvatarTitleDiv>
              <Avatar
                data-test-id="offer-modal-avatar"
                src={avatarSrc}
                width={'100px'}
                customStyle={{
                  backgroundColor: '#ccc',
                  border: `1px solid ${borders.light.primary}`,
                }}
              />
              <TitleDiv>{`Edit Your ${currentUserIsGenerous ? 'Offer' : 'Request'}`}</TitleDiv>
            </AvatarTitleDiv>
            <Headline
              style={{
                color: getPatternModal().headerColor,
                marginTop: 20,
                fontSize: 16,
                lineHeight: '21px',
                letterSpacing: '-0.3px',
              }}
              data-test-id="profile-offer-headline"
              size="SM"
            >
              <span>
                Edit your offer {isGenerous ? '' : 'request'} to <strong>{username}</strong>
              </span>
            </Headline>
          </HeaderDiv>
          <Content>
            <React.Fragment>
              <PriceContainer
                // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
                hasErrors={hasErrors}
              >
                <CurrencyTip
                  offerPrice={colorType(offerPrice, hasErrors)}
                  style={{
                    paddingLeft: adjustStylePerDigit(offerPrice).paddingLeft,
                  }}
                >
                  <CurrencyLabelSpan
                    customCurrencyLabelStyle={{
                      fontSize: 60,
                      fontWeight: 400,
                      lineHeight: '50px',
                      letterSpacing: -0.3,
                      [breakpoints.down('sm')]: { fontSize: '50px', lineHeight: '50px' },
                    }}
                  >
                    {currencyInitial}
                    {currLabel}
                  </CurrencyLabelSpan>
                  <PriceInput
                    inputRef={priceInput}
                    data-test-id="offer-price-input"
                    hasErrors={hasErrors}
                    value={offerPrice}
                    onChange={inputOnChange}
                    validateTextFn={validateTextInput}
                    placeholder={defaultValue}
                    inputProps={{
                      style: {
                        ...getInputProps().style,
                        marginLeft: adjustStylePerDigit(offerPrice).marginLeft,
                        textAlign: 'left',
                        fontSize: 60,
                        fontWeight: 400,
                        lineHeight: '50px',
                        letterSpacing: -0.3,
                      },
                    }}
                    minWidth={IsMobileViewPort ? 100 : 120}
                    responsiveFontSize={`
                      @media (max-width: 1023.95px) {
                        font-size: 50px !important;
                        line-height: 50px !important;
                      }
                   `}
                  />
                </CurrencyTip>
              </PriceContainer>
              <EditAmountRange hasErrors={hasErrors}>{offerAmountMessage}</EditAmountRange>
            </React.Fragment>
            <ButtonDiv hasErrors={hasErrors}>
              <ButtonSet align="center">
                {isOfferAmountIsMinimumOrMaximum ? (
                  <PrimaryButton
                    size="small"
                    color="red"
                    label="Cancel and Send New Offer"
                    onClick={cancelAndShowANewOfferModal}
                    withIconLeft={
                      <CrossIcon
                        fill={white}
                        customStyle={{ width: 15, height: 15, marginRight: 3, position: 'relative', top: -1.5 }}
                      />
                    }
                    customStyle={{
                      display: 'flex',
                      alignItems: 'center',
                      width: 'inherit',
                      height: 42,
                    }}
                  />
                ) : (
                  <Button
                    id={buttonID}
                    buttonRef={sendOfferBtn}
                    disabled={hasErrors || isOfferSending || parseInt(currentOffer) === parseInt(offerPrice)}
                    buttonType="error"
                    onClick={handleSubmit}
                    type="submit"
                    customStyle={{
                      fontSize: 18,
                      lineHeight: '16px',
                      letterSpacing: -0.3,
                      padding: '12px 28px',
                      borderRadius: '4px !important',
                    }}
                  >
                    <WYPButtonIcon style={{ marginRight: 6 }} />
                    {isOfferSending
                      ? `Submitting ${status(currentUserIsGenerous)}...`
                      : `Send New Offer ${!isGenerous ? 'Request' : ''}`}
                  </Button>
                )}
              </ButtonSet>
              {currentOffer && (
                <React.Fragment>
                  <CenterDiv>
                    <strong>&mdash; OR &mdash;</strong>
                  </CenterDiv>
                  <span>
                    <ButtonSet align="center">
                      <TextLink
                        linkRef={sendSuggestLink}
                        data-test-id="offer-suggest-link"
                        to="/"
                        disabled={hasErrors || isOfferSending}
                        onClick={e => onHandleSuggestion(e)}
                        type="submit"
                        style={{
                          color: '#E21D4A',
                          textDecoration: 'underline',
                          fontSize: isMobile ? 16 : 18,
                          lineHeight: '23px',
                          letterSpacing: -0.5,
                        }}
                      >
                        {' '}
                        Ask {sex === 'Male' ? 'him' : 'her'} to suggest an offer amount
                      </TextLink>
                    </ButtonSet>
                  </span>
                </React.Fragment>
              )}
              <ButtonSet align="center">
                <Button
                  buttonType="chromeless"
                  buttonRef={sendCancelBtn}
                  data-test-id={`${buttonID}Cancel`}
                  disabled={isOfferSending}
                  onClick={e => {
                    e.preventDefault();
                    closeModal();
                  }}
                  customStyle={{
                    color: borders.light.secondary,
                    fontWeight: 'normal',
                    textDecoration: 'none',
                    padding: '20px 0',
                  }}
                >
                  {' '}
                  Cancel
                </Button>
              </ButtonSet>
            </ButtonDiv>
          </Content>
        </Form>
        <OfferModalFooter />
      </Fragment>
    </ModalWithPattern>
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    profile: state.profile,
  };
};

export default React.memo(connect(mapStateToProps, {})(OfferEdit));
